import { ReactNode, useMemo } from 'react';
import {
  Navigate,
  Link as RouterLink,
  useSearchParams,
} from 'react-router-dom';

import {
  Button,
  Flex,
  Link,
  Text,
  buttonClassNames,
} from '@spotify-internal/encore-web';
import { AuthScreen, useAlert, useAuth } from '@spotify/confidence-core-react';
import { withPageImpression } from '@spotify/plugin-confidence-tracking-react';

import { invitedUserAuthorizationParams } from '../../utils/authLoginParams';
import {
  clearInvitationLoginReturn,
  persistInvitationLoginReturn,
} from '../../utils/invitationLoginReturn';
import { getLoginAuthErrorMessage } from '../SignUp/signupUtils';
import { emailLink } from '../utils';

const INVITATION_MISMATCH_ERROR =
  'the specified account is not allowed to accept the current invitation';

const invitationActionClassNames = {
  primary: buttonClassNames({
    variant: 'primary',
    colorSet: 'inverted',
    size: 'large',
  }),
  secondary: buttonClassNames({
    variant: 'secondary-bordered',
    size: 'large',
  }),
};

type LinkAction = {
  href: string;
  label: string;
};

const supportAction = (message: string): LinkAction => {
  return {
    href: emailLink(
      'Login issue',
      `Page: ${encodeURIComponent(
        global.window.location.href,
      )}%0D%0AError:${message}`,
    ),
    label: 'Contact support',
  };
};

const InvitationPageComponent = () => {
  const alert = useAlert();
  const [searchParams] = useSearchParams();
  const invitation = searchParams.get('invitation');
  const organization = searchParams.get('organization');

  const {
    isAuthenticated,
    isLoading: loading,
    loginWithRedirect,
    error,
  } = useAuth();

  const handleLogin = async () => {
    if (searchParams.get('invitation')) {
      persistInvitationLoginReturn(searchParams.toString());
    }

    try {
      await loginWithRedirect({
        appState: { returnTo: '/' },
        ...(invitation
          ? {
              authorizationParams: {
                ...invitedUserAuthorizationParams,
                ...(organization ? { invitation, organization } : {}),
              },
            }
          : {}),
      });
    } catch (loginError) {
      alert.post({
        severity: 'error',
        message: getLoginAuthErrorMessage(loginError),
      });
    }
  };

  const { title, message, loginActionLabel, primaryAction, secondaryAction } =
    useMessageProps({
      error,
    });

  const loginButtonLabel =
    loginActionLabel ?? (invitation ? 'Accept invitation' : 'Sign up');

  if (isAuthenticated && !invitation) {
    clearInvitationLoginReturn();
    return <Navigate to="/" replace />;
  }

  return (
    <AuthScreen.Root>
      <AuthScreen.Main>
        <Flex direction="column" gap="looser-3" alignItems="center">
          <RouterLink to="/" aria-label="Go to homepage">
            <AuthScreen.Logo />
          </RouterLink>
          <Flex direction="column" gap="tighter-3" alignItems="center">
            <AuthScreen.Title>{title}</AuthScreen.Title>
            {message && (
              <AuthScreen.Description>{message}</AuthScreen.Description>
            )}
          </Flex>
          <AuthScreen.Actions>
            {primaryAction ? (
              <Link
                href={primaryAction.href}
                className={invitationActionClassNames.primary}
              >
                {primaryAction.label}
              </Link>
            ) : (
              <Button
                variant="primary"
                colorSet="inverted"
                onClick={handleLogin}
                size="large"
                disabled={loading}
                isLoading={loading}
                loadingAriaLabel={loginButtonLabel}
              >
                {loginButtonLabel}
              </Button>
            )}
            {secondaryAction && (
              <Link
                href={secondaryAction.href}
                className={invitationActionClassNames.secondary}
              >
                {secondaryAction.label}
              </Link>
            )}
          </AuthScreen.Actions>
        </Flex>
      </AuthScreen.Main>
    </AuthScreen.Root>
  );
};

export const InvitationPage = withPageImpression(InvitationPageComponent, {
  pageId: 'invitation',
});

type MessageProps = {
  title: ReactNode;
  message: ReactNode;
  loginActionLabel?: string;
  primaryAction?: LinkAction;
  secondaryAction?: LinkAction;
};

function useMessageProps({ error }: { error: Error | undefined }) {
  const [searchParams] = useSearchParams();
  const hasInvitation = !!searchParams.get('invitation');
  const organization = searchParams.get('organization_name');

  const props = useMemo((): MessageProps => {
    switch (true) {
      case error && hasInvitation && error.message.includes('not found'):
        return {
          title: "We couldn't find that invite",
          message: (
            <span>
              If you already have an account, you can log in to Confidence
              instead. If you can&apos;t log in, the invite might have expired.
              Ask for a new one and try again.
            </span>
          ),
          primaryAction: {
            href: '/',
            label: 'Go to login page',
          },
          secondaryAction: supportAction(error.message),
        };
      case error &&
        hasInvitation &&
        error.message.includes(INVITATION_MISMATCH_ERROR):
        return {
          title: 'This invite is for a different email',
          message: (
            <span>
              The email you used to log in doesn&apos;t match the email on the
              invite. Ask your admin to create another invite.
            </span>
          ),
          loginActionLabel: 'Use a different account',
        };
      case error && error.message.includes('Timeout'):
        return {
          title: 'Login session timed out',
          message: <span>Try again.</span>,
        };

      case error && error.message.includes('Invalid domain'):
        return {
          title: "This email domain can't be used here",
          message: (
            <span>
              The account doesn&apos;t allow emails from this domain to log in.
              Contact your account admin.
            </span>
          ),
        };
      case hasInvitation:
        return {
          title: 'Welcome to Confidence',
          message: (
            <span>
              You have been invited to{' '}
              {organization ? (
                <span>
                  the{' '}
                  <Text
                    as="span"
                    variant="bodyMedium"
                    semanticColor="textBrightAccent"
                  >
                    {organization}
                  </Text>{' '}
                  workspace
                </span>
              ) : (
                'Confidence'
              )}
              . Accept the invitation to get started.
            </span>
          ),
        };

      default:
        return {
          title: (
            <span>
              Have an idea.
              <br /> Test it. Ship it. <br /> With Confidence.
            </span>
          ),
          message: null,
        };
    }
  }, [error, hasInvitation, organization]);
  return props;
}
